import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';
import { Gift, Copy, Check, Users, TrendingUp, Clock } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import AppHeader from '../components/AppHeader';
import Breadcrumb from '../components/Breadcrumb';
import { useToast } from '../components/Toast';
import {
  PageContainer, PageHeader, PageTitle, PageSubtitle,
  Card, Flex, Badge, LoadingContainer, Spinner
} from '../components/ui';
import { theme } from '../styles/theme';
import api from '../services/api';

export default function ReferralPage() {
  const { tenantId }  = useParams();
  const { t }         = useTranslation('billing');
  const toast         = useToast();
  const qrRef         = useRef(null);

  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(false);
  const [copied, setCopied]   = useState(false);

  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const breadcrumbItems = [
    { label: t('home', { ns: 'common' }), path: `/dashboard/${tenantId}` },
    { label: t('referral.title'), icon: '🎁' }
  ];

  useEffect(() => { load(); }, [tenantId]); // eslint-disable-line

  const load = async () => {
    try {
      const res = await api.get('/referral/stats');
      setData(res.data);
    } catch (e) { console.error(e); setError(true); }
    finally { setLoading(false); }
  };

  const copyLink = () => {
    navigator.clipboard.writeText(data.referralLink);
    setCopied(true);
    toast.success(t('referral.copied'));
    setTimeout(() => setCopied(false), 2000);
  };

  const shareWa = () => {
    const msg = encodeURIComponent(
      `Estou usando o Libben AI para automatizar meu atendimento com IA. Experimente grátis e ganhe +500 mensagens: ${data.referralLink}`
    );
    window.open(`https://wa.me/?text=${msg}`, '_blank');
  };

  // Gera QR code via API pública (sem dependência extra)
  const qrUrl = data?.referralLink
    ? `https://api.qrserver.com/v1/create-qr-code/?size=160x160&data=${encodeURIComponent(data.referralLink)}`
    : null;

  if (loading) return (
    <>
      <AppHeader title={t('referral.title')} />
      <LoadingContainer><Spinner size="24px" /></LoadingContainer>
    </>
  );

  if (error) return (
    <>
      <AppHeader title={t('referral.title')} />
      <LoadingContainer>{t('loadError', { ns: 'common', defaultValue: 'Erro ao carregar. Tente novamente.' })}</LoadingContainer>
    </>
  );

  return (
    <>
      <AppHeader title={t('referral.title')} />
      <BreadcrumbBar><Breadcrumb items={breadcrumbItems} /></BreadcrumbBar>
      <PageContainer>
        <PageHeader>
          <div>
            <PageTitle>🎁 {t('referral.title')}</PageTitle>
            <PageSubtitle>{t('referral.subtitle')}</PageSubtitle>
          </div>
        </PageHeader>

        <TwoCol>
          {/* Coluna esquerda — link + QR */}
          <Card>
            <SectionLabel>{t('referral.yourLink')}</SectionLabel>
            <LinkRow>
              <LinkInput value={data?.referralLink || ''} readOnly />
              <CopyBtn onClick={copyLink}>
                {copied ? <Check size={14} /> : <Copy size={14} />}
                {copied ? t('referral.copied') : t('referral.copyLink')}
              </CopyBtn>
            </LinkRow>

            <ShareRow>
              <WaBtn onClick={shareWa}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                {t('referral.shareWa')}
              </WaBtn>
            </ShareRow>

            {qrUrl && (
              <QrSection>
                <QrLabel>QR Code</QrLabel>
                <img ref={qrRef} src={qrUrl} alt="QR Code" width={160} height={160} />
              </QrSection>
            )}

            {/* Como funciona */}
            <HowItWorks>
              <SectionLabel>{t('referral.howItWorks')}</SectionLabel>
              {[
                t('referral.step1'),
                t('referral.step2'),
                t('referral.step3')
              ].map((step, i) => (
                <Step key={i}>
                  <StepNum>{i + 1}</StepNum>
                  <StepText>{step}</StepText>
                </Step>
              ))}
            </HowItWorks>
          </Card>

          {/* Coluna direita — stats + histórico */}
          <div>
            <StatsGrid>
              <StatCard>
                <StatIcon><Users size={20} color={theme.colors.primary} /></StatIcon>
                <StatValue>{data?.stats?.total || 0}</StatValue>
                <StatLabel>{t('referral.total')}</StatLabel>
              </StatCard>
              <StatCard>
                <StatIcon><Check size={20} color={theme.colors.success} /></StatIcon>
                <StatValue>{data?.stats?.active || 0}</StatValue>
                <StatLabel>{t('referral.active')}</StatLabel>
              </StatCard>
              <StatCard>
                <StatIcon><Clock size={20} color={theme.colors.warning} /></StatIcon>
                <StatValue>{data?.stats?.pending || 0}</StatValue>
                <StatLabel>{t('referral.pending')}</StatLabel>
              </StatCard>
              <StatCard $highlight>
                <StatIcon><TrendingUp size={20} color={theme.colors.primary} /></StatIcon>
                <StatValue>+{data?.stats?.totalBonusEarned || 0}</StatValue>
                <StatLabel>{t('referral.bonusEarned')} ({t('referral.messages')})</StatLabel>
              </StatCard>
            </StatsGrid>

            <Card style={{ marginTop: theme.spacing[4] }}>
              <SectionLabel>{t('referral.statsTitle')}</SectionLabel>
              {!data?.referrals?.length ? (
                <EmptyReferrals>{t('referral.noReferrals')}</EmptyReferrals>
              ) : (
                <ReferralList>
                  {data.referrals.map((r, i) => (
                    <ReferralItem key={i}>
                      <ReferralId>{r.tenantId}</ReferralId>
                      <Flex gap={theme.spacing[2]} align="center">
                        <Badge variant={r.status === 'active' ? 'success' : 'default'}>
                          {r.status === 'active' ? t('referral.statusActive') : t('referral.statusPending')}
                        </Badge>
                        <ReferralDate>
                          {new Date(r.createdAt).toLocaleDateString('pt-BR')}
                        </ReferralDate>
                      </Flex>
                    </ReferralItem>
                  ))}
                </ReferralList>
              )}
            </Card>
          </div>
        </TwoCol>
      </PageContainer>
    </>
  );
}

const BreadcrumbBar = styled.div`background: ${theme.colors.white}; padding: 0 ${theme.spacing[6]}; border-bottom: 1px solid ${theme.colors.border};`;
const TwoCol = styled.div`display: grid; grid-template-columns: 1fr 1fr; gap: ${theme.spacing[6]}; @media(max-width: 768px) { grid-template-columns: 1fr; }`;
const SectionLabel = styled.div`font-size: ${theme.typography.xs}; font-weight: ${theme.typography.semibold}; color: ${theme.colors.textMuted}; text-transform: uppercase; letter-spacing: .06em; margin-bottom: ${theme.spacing[3]};`;
const LinkRow = styled.div`display: flex; gap: ${theme.spacing[2]}; margin-bottom: ${theme.spacing[3]};`;
const LinkInput = styled.input`flex: 1; padding: ${theme.spacing[2]} ${theme.spacing[3]}; border: 1.5px solid ${theme.colors.border}; border-radius: ${theme.radii.md}; font-size: ${theme.typography.xs}; font-family: monospace; color: ${theme.colors.textSecondary}; background: ${theme.colors.gray50}; outline: none;`;
const CopyBtn = styled.button`display: flex; align-items: center; gap: ${theme.spacing[1]}; padding: ${theme.spacing[2]} ${theme.spacing[3]}; background: ${theme.colors.primary}; color: white; border: none; border-radius: ${theme.radii.md}; font-size: ${theme.typography.xs}; font-weight: ${theme.typography.semibold}; cursor: pointer; white-space: nowrap; &:hover { background: ${theme.colors.primaryHover}; }`;
const ShareRow = styled.div`display: flex; gap: ${theme.spacing[2]}; margin-bottom: ${theme.spacing[4]};`;
const WaBtn = styled.button`display: flex; align-items: center; gap: ${theme.spacing[2]}; padding: ${theme.spacing[2]} ${theme.spacing[4]}; background: ${theme.colors.channels.whatsapp}; color: white; border: none; border-radius: ${theme.radii.md}; font-size: ${theme.typography.sm}; font-weight: ${theme.typography.semibold}; cursor: pointer; &:hover { background: ${theme.colors.channels.whatsappHover}; }`;
const QrSection = styled.div`display: flex; flex-direction: column; align-items: center; gap: ${theme.spacing[2]}; padding: ${theme.spacing[4]} 0; border-top: 1px solid ${theme.colors.border}; margin-top: ${theme.spacing[4]};`;
const QrLabel = styled.div`font-size: ${theme.typography.xs}; color: ${theme.colors.textMuted}; font-weight: ${theme.typography.semibold};`;
const HowItWorks = styled.div`margin-top: ${theme.spacing[5]}; padding-top: ${theme.spacing[4]}; border-top: 1px solid ${theme.colors.border};`;
const Step = styled.div`display: flex; align-items: center; gap: ${theme.spacing[3]}; margin-bottom: ${theme.spacing[2]};`;
const StepNum = styled.div`width: 24px; height: 24px; border-radius: 50%; background: ${theme.colors.primary}; color: white; font-size: 12px; font-weight: 700; display: flex; align-items: center; justify-content: center; flex-shrink: 0;`;
const StepText = styled.div`font-size: ${theme.typography.sm}; color: ${theme.colors.textSecondary};`;
const StatsGrid = styled.div`display: grid; grid-template-columns: 1fr 1fr; gap: ${theme.spacing[3]};`;
const StatCard = styled(Card)`text-align: center; padding: ${theme.spacing[4]}; ${p => p.$highlight && `border-color: ${theme.colors.primary}; background: ${theme.colors.primaryLight};`}`;
const StatIcon = styled.div`display: flex; justify-content: center; margin-bottom: ${theme.spacing[2]};`;
const StatValue = styled.div`font-size: 28px; font-weight: 800; color: ${theme.colors.textPrimary};`;
const StatLabel = styled.div`font-size: ${theme.typography.xs}; color: ${theme.colors.textMuted}; margin-top: 4px;`;
const EmptyReferrals = styled.div`text-align: center; padding: ${theme.spacing[6]}; color: ${theme.colors.textMuted}; font-size: ${theme.typography.sm};`;
const ReferralList = styled.div`display: flex; flex-direction: column; gap: ${theme.spacing[2]};`;
const ReferralItem = styled.div`display: flex; align-items: center; justify-content: space-between; padding: ${theme.spacing[2]} ${theme.spacing[3]}; background: ${theme.colors.gray50}; border-radius: ${theme.radii.md};`;
const ReferralId = styled.div`font-size: ${theme.typography.xs}; font-family: monospace; color: ${theme.colors.textSecondary};`;
const ReferralDate = styled.div`font-size: 11px; color: ${theme.colors.textMuted};`;
